import useNaverADsScript, { NaverADsType } from "@repo/hooks/lib/useNaverADsScript";
import type { IOrdersEdgesNode } from "@repo/shopify/src/queries/customer-gql";
import type { Metaobject, SelectedOption } from "@shopify/hydrogen-react/storefront-api-types";
import parse from "html-react-parser";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@atoms/Button";
import CompareAtPrice from "@atoms/CompareAtPrice";
import Select from "@atoms/Select";

import ParseContent from "@molecules/ParseContent";

import useCart from "@hooks/reducerHooks/useCart";
import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";
import usePreOrder from "@hooks/utilsHooks/usePreOrder";

import { NOTIFICATIONS } from "@utils/notifications";
import type { TProduct } from "@utils/product";

const DEFAULT_OPTION = { value: -1, name: "Select Size" };

export default function PreOrderDetail({ product }: { product: TProduct }) {
  const { customer } = useUser();
  const { cart, addCartLine, addCartLoading, removeCartLineBase } = useCart();
  const { openToast, openModal, closeModal, setModalProgress } = useLayout();
  const { t } = useTranslation();
  const { tryAppendNaverADsScript } = useNaverADsScript();

  const options = useMemo(() => getAvailableSize(product), [product]);

  const [selectedVariant, setSelectedVariant] = useState<{ value: number | string; name: string }>(DEFAULT_OPTION);
  const [available, setAvailable] = useState(true);

  const { title, description } = usePreOrder(product.preorder?.reference as Metaobject);

  useEffect(() => {
    const quantityLimit = parseInt(product.quantityLimit?.value || "-1");

    if (selectedVariant.value !== DEFAULT_OPTION.value) {
      // 현재 고른 variant가 카트에 이미 담겨있는지 확인
      const variantLine = cart.lines.edges.find(({ node }) => node.merchandise.id === selectedVariant.value)?.node;
      // 이미 담겨있으면 구매 가능한 재고와 이미 담겨있는 재고를 비교
      if (variantLine && (variantLine.quantity ?? 0) >= (variantLine.merchandise.quantityAvailable ?? 0)) {
        // 구매할 수 있는 개수 이상으로 담겨있으면 available state를 false로 바꾼다.
        // TODO: available==false일 때 selector는 기능하게 해도 괜찮을지?
        setAvailable(false);
        return;
      }
    }

    // product에 quantity limit이 정해져있지 않으면 구매 개수 제한을 확인하지 않는다.
    if (quantityLimit < 0) {
      setAvailable(true);
      return;
    }

    // 카트에 담긴 quantity를 가져온다.
    const cartQuantity =
      cart.lines.edges.reduce((accumulator, { node }) => {
        if (product.id === node.merchandise.product.id) {
          /* eslint-disable-next-line no-param-reassign */
          accumulator += node.quantity;
        }

        return accumulator;
      }, 0) || 0;

    // 카트에 담긴 quantity가 limit 이상이면 available state를 false로 바꿔준다.
    if (cartQuantity >= quantityLimit) {
      setAvailable(false);
      return;
    }

    // 고객의 order history에서 product quantity 가져온다.
    const orderQuantity = customer.orders.edges
      .filter(({ node }: { node: IOrdersEdgesNode }) => node.exclude_limit?.value !== "true")
      .reduce((qtySum, { node }) => {
        const quantityInOrder = node.lineItems.edges.reduce((lineItemQtySum, { node: orderLineItem }) => {
          if (product.id === orderLineItem.variant?.product.id) {
            /* eslint-disable-next-line no-param-reassign */
            lineItemQtySum += orderLineItem.quantity;
          }
          return lineItemQtySum;
        }, 0);
        return qtySum + quantityInOrder;
      }, 0);

    // cart quantity + order quantity가 product limit 이상이면 available state를 false로 바꿔준다.
    if (cartQuantity + orderQuantity >= quantityLimit) {
      setAvailable(false);
      return;
    }

    // cart quantity, order quantity check를 통과했으면 구매가 가능한 것으로 보고 available state를 true로 바꿔준다.
    setAvailable(true);
  }, [product, customer.orders, cart, selectedVariant]);

  /** 상품 option 변경 */
  const handleProductOption = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value, selectedIndex } = event.target;
    const isDefault = value === String(DEFAULT_OPTION.value);
    setSelectedVariant(isDefault ? DEFAULT_OPTION : { value, name: options[selectedIndex - 1].name });
  };

  /** 상품 카트 담기 */
  const handleAddToCart = async () => {
    // addCart 하는 동안
    if (addCartLoading) return;

    if (!available) return;
    if (selectedVariant.value === DEFAULT_OPTION.value) {
      openToast(NOTIFICATIONS.WARN_SELECT_SIZE);
      return;
    }

    // 프리오더 상품을 담을 때 혹은 프리오더 상품이 카트에 있을 경우
    if (cart.lines.edges.length) {
      openModal({
        title: "Preorder Notice",
        description:
          "프리오더 상품 주문은 단건으로만 가능합니다.\n해당 상품을 카트에 담을 경우 기존 카트의 일반 상품은 삭제됩니다.",
        cancelButton: {
          label: "Cancel",
          onClick: () => {
            closeModal();
          },
        },
        confirmButton: {
          label: "Okay",
          onClick: () => {
            setModalProgress(true);

            // 현재 카트 상품 지우고 상품 담기
            void removeCartLineBase({
              variables: { cartId: cart.id, lineIds: cart.lines.edges.map(({ node }) => node.id) },
            }).then(() => {
              void addCartLine({
                variables: { cartId: cart.id, lines: [{ merchandiseId: selectedVariant.value, quantity: 1 }] },
              }).then(() => {
                closeModal();
              });
            });

            tryAppendNaverADsScript({ type: NaverADsType.Cart, value: "0" });
          },
        },
      });

      return;
    }

    await addCartLine({
      variables: { cartId: cart.id, lines: [{ merchandiseId: selectedVariant.value, quantity: 1 }] },
    });

    tryAppendNaverADsScript({ type: NaverADsType.Cart, value: "0" });
  };

  const compareAtPrice = useMemo(
    () => parseInt(product.variants.edges[0].node.compareAtPrice?.amount ?? "0"),
    [product],
  );
  const price = useMemo(() => parseInt(product.variants.edges[0].node.price.amount ?? "0"), [product]);

  return (
    <>
      <section className="mt-5 flex w-full flex-col items-center justify-center gap-5 px-5">
        <h2 className="font-tune text-center text-3xl leading-tight">{parse(title || "")}</h2>
        <section className="w-full">
          <ParseContent html={description || ""} />
        </section>
      </section>
      {product.availableForSale ? (
        <div className="w-mobileFloatingBar rounded-2.5 bg-floating-bar pc:w-[calc((100%_-_40px)*0.4*0.8)] pc:min-w-160 pc:flex-row pc:gap-5 sticky bottom-10 z-10 mx-auto mt-10 flex flex-col items-center justify-between gap-2.5 p-2.5 backdrop-blur-2xl">
          <div className="pc:text-left text-center">
            {product.title}
            <br />
            <CompareAtPrice originalPrice={compareAtPrice} salePrice={price} />
          </div>
          <div className="flex gap-2">
            <Select
              id="sizeSelector"
              onChange={handleProductOption}
              options={options}
              placeholder="Select Size"
              selected={selectedVariant}
            />
            <Button
              disabled={!available}
              onClick={() => {
                handleAddToCart().catch(() => 0);
              }}
              styleType="primary"
            >
              Add to Cart
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

const getAvailableSize = (product: TProduct) => {
  return product.variants.edges
    .filter(({ node }) => node.availableForSale)
    .map(({ node }) => ({
      value: node.id,
      name:
        node.selectedOptions.find((option: SelectedOption) => ["Size", "사이즈"].includes(option.name))?.value ?? "",
    }));
};
